// Finance Books — Transaction Documents (Invoice / Bill / Estimate / SO / PO / GRN / Credit & Debit Notes).
// Shared DocList + DocForm pattern parameterised by config.
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import fb from '../api';
import { PageHeader, Btn, Drawer, Input, Select, Textarea, Table, Pill,
  fmtMoney, fmtDate, todayISO, statusTone, SOFT, PANEL } from '../ui';
import ProjectBudgetPicker from './BudgetPicker';

// ────────────────────────────────────────────────────────────────────────────
// Generic doc list
// ────────────────────────────────────────────────────────────────────────────
function DocList({ title, subtitle, listPath, getPath, newLabel, party, partyKey, dataTestid,
                   onNew, extraColumns = [], extraAction }) {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const reload = async () => setRows(await fb.get(listPath));
  useEffect(() => { reload(); }, [listPath]);

  const openDetail = async (r) => {
    const d = await fb.get(`${getPath}/${r.id}`);
    setOpenDoc(d); setOpenId(r.id);
  };

  return (
    <div data-testid={dataTestid}>
      <PageHeader
        title={title} subtitle={`${subtitle} · ${rows.length} records`}
        right={<Btn variant="accent" onClick={onNew} data-testid={`fb-new-${title.toLowerCase().replace(/\s+/g, '-')}`}>+ {newLabel}</Btn>}
      />
      <Table
        columns={[
          { key: 'doc_no', label: '#', render: (r) => <button onClick={() => openDetail(r)} className="font-mono text-xs text-white/80 hover:text-white underline-offset-2 hover:underline">{r.doc_no}</button> },
          { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
          { key: party, label: party, render: (r) => r[partyKey] },
          ...extraColumns,
          { key: 'grand_total', label: 'Total', align: 'right', render: (r) => <span className="tabular-nums text-white font-medium">{fmtMoney(r.grand_total)}</span> },
          { key: 'balance_due', label: 'Balance', align: 'right', render: (r) => r.balance_due !== undefined ? <span className="tabular-nums text-orange-300">{fmtMoney(r.balance_due)}</span> : <span className="text-white/30">—</span> },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
          ...(extraAction ? [{ key: 'act', label: '', render: extraAction(reload) }] : []),
        ]}
        rows={rows}
        empty={`No ${title.toLowerCase()} yet.`}
        testidPrefix={title.toLowerCase().replace(/\s+/g, '-')}
      />
      <DocDetailDrawer doc={openDoc} open={!!openId} onClose={() => { setOpenId(null); setOpenDoc(null); }} kind={title} reload={reload} />
    </div>
  );
}

function DocDetailDrawer({ doc, open, onClose, kind, reload }) {
  if (!doc) return null;
  const onVoid = async () => {
    if (!window.confirm(`Void ${doc.doc_no}? This will reverse all journal entries.`)) return;
    try {
      const ep = kind.toLowerCase().includes('bill') ? `/bills/${doc.id}/void`
               : kind.toLowerCase().includes('invoice') ? `/invoices/${doc.id}/void`
               : null;
      if (!ep) return;
      await fb.post(ep);
      toast.success('Voided'); onClose(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Void failed'); }
  };
  return (
    <Drawer open={open} onClose={onClose} title={`${kind} ${doc.doc_no}`}>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className={`${SOFT} rounded-lg p-4`}>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">Party</div>
          <div className="text-white mt-1">{doc.customer_name || doc.vendor_name}</div>
          <div className="text-white/40 text-xs mt-1">{doc.customer_gstin || doc.vendor_gstin || ''}</div>
        </div>
        <div className={`${SOFT} rounded-lg p-4`}>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">Status</div>
          <div className="mt-2"><Pill tone={statusTone(doc.status)}>{doc.status}</Pill></div>
          <div className="text-white/40 text-xs mt-2">{fmtDate(doc.date)}{doc.due_date && ` · Due ${fmtDate(doc.due_date)}`}</div>
        </div>
      </div>
      <div className={`${PANEL} rounded-lg overflow-hidden`}>
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-white/55 text-[10px] tracking-[0.2em] uppercase">
            <tr><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Tax</th><th className="px-3 py-2 text-right">Total</th></tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {(doc.lines || []).map((l, i) => (
              <tr key={i}>
                <td className="px-3 py-2 text-white/85">{l.name}{l.hsn_sac && <span className="text-white/30 ml-2 text-[10px]">HSN {l.hsn_sac}</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums">{l.qty}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(l.rate)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-white/50">{fmtMoney(l.tax_amount)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 space-y-1 text-sm">
        <Row k="Subtotal" v={fmtMoney(doc.subtotal)} />
        {doc.cgst > 0 && <Row k="CGST" v={fmtMoney(doc.cgst)} />}
        {doc.sgst > 0 && <Row k="SGST" v={fmtMoney(doc.sgst)} />}
        {doc.igst > 0 && <Row k="IGST" v={fmtMoney(doc.igst)} />}
        {doc.shipping > 0 && <Row k="Shipping" v={fmtMoney(doc.shipping)} />}
        {doc.doc_discount > 0 && <Row k="Discount" v={`- ${fmtMoney(doc.doc_discount)}`} />}
        {doc.round_off !== 0 && <Row k="Round Off" v={fmtMoney(doc.round_off)} />}
        <Row k="Grand Total" v={fmtMoney(doc.grand_total)} bold />
        {doc.amount_paid !== undefined && <Row k="Paid" v={fmtMoney(doc.amount_paid)} tone="green" />}
        {doc.balance_due !== undefined && <Row k="Balance Due" v={fmtMoney(doc.balance_due)} tone="orange" />}
      </div>
      {(kind === 'Invoices' || kind === 'Bills') && doc.status !== 'void' && doc.amount_paid === 0 && (
        <div className="flex justify-end mt-5">
          <Btn variant="danger" onClick={onVoid} data-testid="doc-void">Void</Btn>
        </div>
      )}
    </Drawer>
  );
}
function Row({ k, v, bold, tone }) {
  return (
    <div className={`flex justify-between py-1 ${bold ? 'border-t border-white/10 pt-2 mt-2 text-white font-medium text-base' : 'text-white/70'}`}>
      <span>{k}</span>
      <span className={`tabular-nums ${tone === 'green' ? 'text-emerald-300' : tone === 'orange' ? 'text-orange-300' : ''}`}>{v}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Generic doc form (Invoice / Bill / Estimate / SO / PO / Credit Note / Debit Note)
// ────────────────────────────────────────────────────────────────────────────
function DocForm({ open, onClose, kind, partyKind /* 'customer' | 'vendor' */, savePath, onSaved, title, requireBudget = false }) {
  // party list
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    [partyKind === 'customer' ? 'customer_id' : 'vendor_id']: '',
    date: todayISO(),
    due_date: '',
    place_of_supply: '',
    reference: '',
    notes: '',
    shipping: 0,
    doc_discount: 0,
    round_off: 0,
    project_id: '',
    budget_id: '',
    lines: [{ name: '', qty: 1, rate: 0, gst_pct: 18, discount_pct: 0 }],
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      setParties(await fb.get(partyKind === 'customer' ? '/customers' : '/vendors'));
      setItems(await fb.get('/items'));
      setForm((f) => ({
        ...f,
        date: todayISO(),
        lines: [{ name: '', qty: 1, rate: 0, gst_pct: 18, discount_pct: 0 }],
      }));
    })();
  }, [open, partyKind]);

  const addLine = () => setForm({ ...form, lines: [...form.lines, { name: '', qty: 1, rate: 0, gst_pct: 18, discount_pct: 0 }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const updateLine = (i, patch) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], ...patch };
    setForm({ ...form, lines });
  };
  const pickItem = (i, itemId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it) return updateLine(i, { item_id: '', name: '' });
    updateLine(i, {
      item_id: it.id,
      name: it.name,
      hsn_sac: it.hsn_sac,
      rate: partyKind === 'customer' ? it.selling_price : it.purchase_price,
      gst_pct: it.gst_pct,
    });
  };

  // computed preview
  const totals = useMemo(() => {
    const party = parties.find((p) => p.id === form[partyKind === 'customer' ? 'customer_id' : 'vendor_id']);
    const pos = form.place_of_supply || party?.state_code || '';
    let sub = 0, cgst = 0, sgst = 0, igst = 0;
    form.lines.forEach((l) => {
      const gross = (Number(l.qty) || 0) * (Number(l.rate) || 0);
      const disc = gross * ((Number(l.discount_pct) || 0) / 100);
      const net = gross - disc;
      const tax = net * ((Number(l.gst_pct) || 0) / 100);
      sub += net;
      // Inter-state if org state vs pos differ — assume intra for the preview unless states differ
      // We can't easily compare; just split as cgst/sgst by default. Backend will recompute.
      cgst += tax / 2; sgst += tax / 2;
    });
    const grand = sub + cgst + sgst + (Number(form.shipping) || 0) - (Number(form.doc_discount) || 0) + (Number(form.round_off) || 0);
    return { sub, cgst, sgst, igst, grand };
  }, [form, parties, partyKind]);

  const save = async () => {
    try {
      const partyField = partyKind === 'customer' ? 'customer_id' : 'vendor_id';
      if (!form[partyField]) return toast.error(`${partyKind === 'customer' ? 'Customer' : 'Vendor'} required`);
      if (!form.lines.length || !form.lines[0].name) return toast.error('At least one line required');
      if (requireBudget && (!form.project_id || !form.budget_id)) {
        return toast.error('Project + Budget are mandatory');
      }
      const payload = { ...form };
      payload.shipping = Number(payload.shipping) || 0;
      payload.doc_discount = Number(payload.doc_discount) || 0;
      payload.round_off = Number(payload.round_off) || 0;
      payload.lines = payload.lines.map((l) => ({
        ...l, qty: Number(l.qty) || 0, rate: Number(l.rate) || 0,
        discount_pct: Number(l.discount_pct) || 0, gst_pct: Number(l.gst_pct) || 0,
      }));
      if (!payload.due_date) delete payload.due_date;
      if (!payload.project_id) delete payload.project_id;
      if (!payload.budget_id) delete payload.budget_id;
      await fb.post(savePath, payload);
      toast.success(`${title} created`);
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <Drawer open={open} onClose={onClose} title={`New ${title}`} width="max-w-5xl">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Select label={partyKind === 'customer' ? 'Customer *' : 'Vendor *'}
          value={form[partyKind === 'customer' ? 'customer_id' : 'vendor_id'] || ''}
          onChange={(e) => setForm({ ...form, [partyKind === 'customer' ? 'customer_id' : 'vendor_id']: e.target.value })}
          data-testid="doc-party">
          <option value="">— Select —</option>
          {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Input label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        <Input label="Due Date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
        {requireBudget && (
          <ProjectBudgetPicker
            value={{ project_id: form.project_id, budget_id: form.budget_id }}
            onChange={(v) => setForm({ ...form, ...v })}
            kind={partyKind === 'customer' ? 'sales' : 'purchase'}
            testidPrefix="doc"
          />
        )}
        <Input label="Place of Supply (state code)" value={form.place_of_supply || ''} onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })} />
        <Input label="Reference" value={form.reference || ''} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
        <Input label="Shipping" type="number" value={form.shipping} onChange={(e) => setForm({ ...form, shipping: e.target.value })} />
      </div>

      <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">Line Items</div>
      <div className={`${PANEL} rounded-lg overflow-hidden`}>
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-white/55 text-[10px] tracking-[0.2em] uppercase">
            <tr>
              <th className="px-2 py-2 text-left">Item</th>
              <th className="px-2 py-2 text-right w-20">Qty</th>
              <th className="px-2 py-2 text-right w-28">Rate</th>
              <th className="px-2 py-2 text-right w-20">Disc %</th>
              <th className="px-2 py-2 text-right w-20">GST %</th>
              <th className="px-2 py-2 text-right w-28">Total</th>
              <th className="px-2 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {form.lines.map((l, i) => {
              const gross = (Number(l.qty) || 0) * (Number(l.rate) || 0);
              const net = gross - gross * ((Number(l.discount_pct) || 0) / 100);
              const total = net + net * ((Number(l.gst_pct) || 0) / 100);
              return (
                <tr key={i}>
                  <td className="px-2 py-1">
                    <select className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs w-full text-white"
                      value={l.item_id || ''} onChange={(e) => pickItem(i, e.target.value)} data-testid={`line-item-${i}`}>
                      <option value="">— Custom —</option>
                      {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </select>
                    <input className="mt-1 bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs w-full text-white"
                      value={l.name || ''} placeholder="Description" onChange={(e) => updateLine(i, { name: e.target.value })} data-testid={`line-name-${i}`} />
                  </td>
                  <td className="px-2 py-1"><input className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs w-full text-right tabular-nums text-white" type="number" value={l.qty} onChange={(e) => updateLine(i, { qty: e.target.value })} data-testid={`line-qty-${i}`} /></td>
                  <td className="px-2 py-1"><input className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs w-full text-right tabular-nums text-white" type="number" value={l.rate} onChange={(e) => updateLine(i, { rate: e.target.value })} data-testid={`line-rate-${i}`} /></td>
                  <td className="px-2 py-1"><input className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs w-full text-right tabular-nums text-white" type="number" value={l.discount_pct} onChange={(e) => updateLine(i, { discount_pct: e.target.value })} /></td>
                  <td className="px-2 py-1"><input className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs w-full text-right tabular-nums text-white" type="number" value={l.gst_pct} onChange={(e) => updateLine(i, { gst_pct: e.target.value })} /></td>
                  <td className="px-2 py-1 text-right text-white tabular-nums">{fmtMoney(total)}</td>
                  <td className="px-2 py-1"><button className="text-rose-400/70 hover:text-rose-400 text-sm" onClick={() => removeLine(i)}>×</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Btn variant="ghost" className="mt-2" onClick={addLine} data-testid="doc-add-line">+ Add Line</Btn>

      <div className="grid grid-cols-2 gap-4 mt-5">
        <Textarea label="Notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <div className={`${SOFT} rounded-lg p-4 space-y-1 text-sm`}>
          <Row k="Subtotal" v={fmtMoney(totals.sub)} />
          <Row k="GST (CGST+SGST)" v={fmtMoney(totals.cgst + totals.sgst)} />
          <Row k="Shipping" v={fmtMoney(Number(form.shipping) || 0)} />
          <Row k="Discount" v={`- ${fmtMoney(Number(form.doc_discount) || 0)}`} />
          <Row k="Grand Total (est.)" v={fmtMoney(totals.grand)} bold />
          <p className="text-[10px] text-white/40 italic mt-2">Server recomputes CGST/SGST vs IGST based on place of supply.</p>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="accent" onClick={save} data-testid="doc-save">Save {title}</Btn>
      </div>
    </Drawer>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section exports — one component per nav key
// ────────────────────────────────────────────────────────────────────────────
function makeSection({ title, subtitle, listPath, partyKind, partyKey, party, dataTestid, newLabel, requireBudget = false }) {
  return function Section() {
    const [open, setOpen] = useState(false);
    const [ver, setVer] = useState(0);
    return (
      <>
        <DocList
          key={ver}
          title={title} subtitle={subtitle}
          listPath={listPath} getPath={listPath}
          newLabel={newLabel} party={party} partyKey={partyKey}
          dataTestid={dataTestid}
          onNew={() => setOpen(true)}
        />
        <DocForm
          open={open} onClose={() => setOpen(false)}
          kind={title} title={title.replace(/s$/, '')}
          partyKind={partyKind} savePath={listPath}
          requireBudget={requireBudget}
          onSaved={() => setVer((v) => v + 1)}
        />
      </>
    );
  };
}

export const Estimates = makeSection({
  title: 'Estimates', subtitle: 'Pre-sales quotations', listPath: '/estimates',
  partyKind: 'customer', partyKey: 'customer_name', party: 'Customer',
  dataTestid: 'fb-estimates', newLabel: 'New Estimate',
});
export const SalesOrders = makeSection({
  title: 'Sales Orders', subtitle: 'Confirmed customer orders', listPath: '/sales-orders',
  partyKind: 'customer', partyKey: 'customer_name', party: 'Customer',
  dataTestid: 'fb-sales-orders', newLabel: 'New Sales Order', requireBudget: true,
});
export const Invoices = makeSection({
  title: 'Invoices', subtitle: 'Customer invoices · auto-journaled', listPath: '/invoices',
  partyKind: 'customer', partyKey: 'customer_name', party: 'Customer',
  dataTestid: 'fb-invoices', newLabel: 'New Invoice', requireBudget: true,
});
export const CreditNotes = makeSection({
  title: 'Credit Notes', subtitle: 'Customer refunds & returns', listPath: '/credit-notes',
  partyKind: 'customer', partyKey: 'customer_name', party: 'Customer',
  dataTestid: 'fb-credit-notes', newLabel: 'New Credit Note',
});
export const PurchaseOrders = makeSection({
  title: 'Purchase Orders', subtitle: 'Vendor orders', listPath: '/purchase-orders',
  partyKind: 'vendor', partyKey: 'vendor_name', party: 'Vendor',
  dataTestid: 'fb-purchase-orders', newLabel: 'New Purchase Order', requireBudget: true,
});
export const Bills = makeSection({
  title: 'Bills', subtitle: 'Vendor bills · auto-journaled', listPath: '/bills',
  partyKind: 'vendor', partyKey: 'vendor_name', party: 'Vendor',
  dataTestid: 'fb-bills', newLabel: 'New Bill', requireBudget: true,
});
export const DebitNotes = makeSection({
  title: 'Debit Notes', subtitle: 'Vendor returns & adjustments', listPath: '/debit-notes',
  partyKind: 'vendor', partyKey: 'vendor_name', party: 'Vendor',
  dataTestid: 'fb-debit-notes', newLabel: 'New Debit Note', requireBudget: true,
});

// GRN is a simpler form — items go in, no monetary totals (just qty + rate)
export function GRNs() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ vendor_id: '', lines: [{ name: '', qty: 1, rate: 0 }] });
  const reload = async () => setRows(await fb.get('/grns'));
  useEffect(() => { reload(); (async () => { setVendors(await fb.get('/vendors')); setItems(await fb.get('/items')); })(); }, []);
  const save = async () => {
    try {
      if (!form.vendor_id) return toast.error('Vendor required');
      const payload = { ...form, lines: form.lines.map((l) => ({ ...l, qty: Number(l.qty), rate: Number(l.rate) })) };
      await fb.post('/grns', payload);
      toast.success('GRN created · stock updated');
      setOpen(false); setForm({ vendor_id: '', lines: [{ name: '', qty: 1, rate: 0 }] }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div data-testid="fb-grns">
      <PageHeader title="Goods Receipt Notes (GRN)" subtitle={`${rows.length} GRNs · stock auto-incremented`}
        right={<Btn variant="accent" onClick={() => setOpen(true)}>+ New GRN</Btn>} />
      <Table
        columns={[
          { key: 'doc_no', label: '#', render: (r) => <span className="font-mono text-xs text-white/80">{r.doc_no}</span> },
          { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
          { key: 'vendor_name', label: 'Vendor' },
          { key: 'status', label: 'Status', render: (r) => <Pill tone="green">{r.status}</Pill> },
        ]}
        rows={rows}
        empty="No GRNs yet."
      />
      <Drawer open={open} onClose={() => setOpen(false)} title="New GRN" width="max-w-3xl">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Vendor *" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}>
            <option value="">— Select —</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </Select>
          <Input label="Reference (Vendor DC)" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="mt-4">
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">Items Received</div>
          {form.lines.map((l, i) => (
            <div className="grid grid-cols-12 gap-2 mb-2" key={i}>
              <select className="col-span-5 bg-white/[0.04] border border-white/10 rounded px-2 py-1.5 text-xs text-white" value={l.item_id || ''}
                onChange={(e) => {
                  const it = items.find((x) => x.id === e.target.value);
                  const lines = [...form.lines];
                  lines[i] = it ? { ...l, item_id: it.id, name: it.name, rate: it.purchase_price } : { ...l, item_id: '', name: '' };
                  setForm({ ...form, lines });
                }}>
                <option value="">— Custom —</option>
                {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
              </select>
              <input className="col-span-3 bg-white/[0.04] border border-white/10 rounded px-2 py-1.5 text-xs text-white" placeholder="Name" value={l.name} onChange={(e) => { const lines = [...form.lines]; lines[i] = { ...l, name: e.target.value }; setForm({ ...form, lines }); }} />
              <input className="col-span-2 bg-white/[0.04] border border-white/10 rounded px-2 py-1.5 text-xs text-white text-right" placeholder="Qty" type="number" value={l.qty} onChange={(e) => { const lines = [...form.lines]; lines[i] = { ...l, qty: e.target.value }; setForm({ ...form, lines }); }} />
              <input className="col-span-2 bg-white/[0.04] border border-white/10 rounded px-2 py-1.5 text-xs text-white text-right" placeholder="Rate" type="number" value={l.rate} onChange={(e) => { const lines = [...form.lines]; lines[i] = { ...l, rate: e.target.value }; setForm({ ...form, lines }); }} />
            </div>
          ))}
          <Btn variant="ghost" onClick={() => setForm({ ...form, lines: [...form.lines, { name: '', qty: 1, rate: 0 }] })}>+ Add Item</Btn>
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={save}>Save GRN</Btn></div>
      </Drawer>
    </div>
  );
}
